import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useState,
} from 'react'
import { TDesignChatBotUseCases } from './briefingForm/utils/parseDesignerChatbotConfiguration'
import { IDesignerChatbot } from './DesignerChatbot'
import { TDesignJourney, TOnlineEditorState } from '../_types'

interface IUnifiedEditorData {
  editorFullScreenMode?: {
    isFullScreen: boolean
    setIsFullScreen: Dispatch<SetStateAction<boolean>>
  }
  editorState: TOnlineEditorState
  setEditorState: Dispatch<SetStateAction<TOnlineEditorState>>
  designerChatbotConfigs: Record<TDesignChatBotUseCases, IDesignerChatbot>
  selectedDesignJourney: TDesignJourney
}

const UnifiedEditorDataContext = createContext<IUnifiedEditorData | undefined>(
  undefined
)

export function UnifiedEditorProvider({
  children,
  selectedDesignJourney = 'upload',
  designerChatbotConfigs,
  editorState,
  setEditorState,
}: {
  children: ReactNode
  selectedDesignJourney: TDesignJourney
  designerChatbotConfigs: Record<TDesignChatBotUseCases, IDesignerChatbot>
  editorState: TOnlineEditorState
  setEditorState: Dispatch<SetStateAction<TOnlineEditorState>>
}) {
  const [isFullScreen, setIsFullScreen] = useState(false)

  const providerValue = {
    editorFullScreenMode: { isFullScreen, setIsFullScreen },
    editorState,
    setEditorState,
    designerChatbotConfigs,
    selectedDesignJourney,
  }

  return (
    <UnifiedEditorDataContext.Provider value={providerValue}>
      {children}
    </UnifiedEditorDataContext.Provider>
  )
}

export function useDesignerChatbotData({
  editorHasUploadedFiles,
  editorHasSvgConvertableFile,
}: {
  editorHasUploadedFiles: boolean
  editorHasSvgConvertableFile: boolean
}): {
  designerChatbot: IDesignerChatbot
} {
  const context = useContext(UnifiedEditorDataContext)

  if (context === undefined) {
    throw new Error(
      'useDesignerChatbotData must be used within a UnifiedEditorProvider'
    )
  }

  const { selectedDesignJourney, designerChatbotConfigs } = context

  let chatbotContent: IDesignerChatbot = {
    ...designerChatbotConfigs[selectedDesignJourney || 'upload'],
    size: 'l',
    mode: 'attention',
  }

  if (selectedDesignJourney === 'editor' && editorHasUploadedFiles) {
    chatbotContent = {
      ...(editorHasSvgConvertableFile
        ? designerChatbotConfigs?.editorNoteOnTrial
        : designerChatbotConfigs?.editorNoteOnFilesTypes),
      size: 'm',
      mode: 'attention',
    }
  }

  if (!chatbotContent.text || !chatbotContent.image) {
    return { designerChatbot: null }
  }

  return {
    designerChatbot: chatbotContent || null,
  }
}

export function useEditorFullScreenMode() {
  const context = useContext(UnifiedEditorDataContext)
  if (context === undefined) {
    throw new Error(
      'useEditorFullScreenMode must be used within a EditorDataContext'
    )
  }
  return context.editorFullScreenMode
}

export function useOnlineEditorState() {
  const context = useContext(UnifiedEditorDataContext)
  if (context === undefined) {
    throw new Error(
      'useOnlineEditorState must be used within a EditorDataContext'
    )
  }
  return {
    editorState: context.editorState,
    setEditorState: context.setEditorState,
  }
}
